<template>
  <nav class="pagination flex justify-between items-stretch border-t border-gray-300 my-12 text-gray-700 font-medium">
    <div class="prev">
      <button v-if="currentPage > 0" class="flex items-center" data-testid="v-paginate-prev" @click="$emit('change-page', currentPage - 1)">
        <ArrowLeftIcon class="mr-3 relative -mt-px h-4 w-4" />
        Previous
      </button>
    </div>
    <div class="pages" :class="['hidden', 'sm_block']">
      <div class="overline transition-transform transform" :style="{ transform: `translateX(${pageLinks.indexOf(currentPage) * 100}%)` }" />
      <ul class="flex mb-0">
        <template v-for="(pageLink, index) in pageLinks">
          <li
            v-if="typeof pageLink === 'number'"
            :key="pageLink"
            class="cursor-pointer"
            :class="{ 'hover_text-gray-900': true, 'text-copper-700': pageLink === currentPage }"
            @click="$emit('change-page', pageLink)"
          >
            {{ pageLink + 1 }}
          </li>
          <li v-else :key="`horizontalEllipsis${index}`" class="cursor-default">{{ pageLink }}</li>
        </template>
      </ul>
    </div>
    <div class="next">
      <button
        v-if="currentPage < max - 1"
        class="flex items-center"
        data-testid="v-paginate-next"
        @click="$emit('change-page', currentPage + 1)"
      >
        Next
        <ArrowRightIcon class="ml-3 relative -mt-px h-4 w-4" />
      </button>
    </div>
  </nav>
</template>
<script lang="ts" setup>
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  max: {
    type: Number,
    required: true,
  },
})

defineEmits(['change-page'])

const H_ELLIPSIS = '\u2026'

const pageLinks = computed(() => {
  const pages = new Set([0, 1, 2])
  for (let i = 0; i < 3; i++) {
    pages.add(props.max - 1 - i)
  }
  for (let i = -1; i <= 1; i++) {
    pages.add(props.currentPage + i)
  }
  const pagesArray = Array.from(pages)
    .filter(page => page >= 0 && page < props.max) // clamp range
    .sort((a, b) => a - b) // sort numerically
    .reduce<Array<number | string>>((acc, cur) => {
      // add "..." when there are gaps
      if (acc.length === 0) return [cur]
      const last = acc[acc.length - 1] as number
      if (cur - last == 2) {
        // if the gap is just 1 more, make it a number
        return acc.concat(cur - 1, cur)
      } else if (cur - last > 2) {
        return acc.concat(H_ELLIPSIS, cur)
      }
      return acc.concat(cur)
    }, [])
  return pagesArray
})
</script>

<style lang="stylus" scoped>
.pagination
  ul li
    @apply w-12 text-center pt-5 -mt-px
    .active
      @apply text-copper-700
  .overline
    @apply border-t-2 border-copper-700 relative -mt-px w-12
  .prev, .next
    &:hover
      @apply text-gray-900
    button
      @apply pt-5 px-1 bg-transparent p-0 cursor-pointer
      color inherit
      font-weight inherit
</style>
